<template>
  <section ref="heroRef" class="hero">
    <div class="hero__bg"></div>

    <div ref="nebulaRef" class="hero__nebula"></div>

    <ParticleField
      :count="100"
      :speed="0.2"
      :opacity="0.5"
      color="167, 139, 250"
      :min-size="0.5"
      :max-size="2"
    />

    <ParticleField
      :count="30"
      :speed="0.4"
      :opacity="0.3"
      color="96, 165, 250"
      :min-size="1"
      :max-size="3"
    />

    <div class="hero__content">
      <img
        ref="logoRef"
        src="/Imgs/digitalizando.svg"
        alt="Digitalizando"
        class="hero__logo"
      />
      <img
        ref="astronautRef"
        src="/Imgs/Astronauta.png"
        alt="Astronauta explorando o universo digital"
        class="hero__astronaut"
      />
    </div>

    <div ref="scrollIndicatorRef" class="hero__scroll-indicator">
      <div class="hero__scroll-line"></div>
    </div>

    <div class="hero__transition-out"></div>
  </section>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted } from "vue";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import ParticleField from "./ParticleField.vue";
import { usePreloader } from "../composables/usePreloader";

gsap.registerPlugin(ScrollTrigger);

const { state: preloaderState } = usePreloader();

const heroRef = ref(null);
const nebulaRef = ref(null);
const logoRef = ref(null);
const astronautRef = ref(null);
const scrollIndicatorRef = ref(null);

let ctx;
let revealPlayed = false;

function setupScrollAnimations() {
  gsap.to(nebulaRef.value, {
    y: 150,
    ease: "none",
    scrollTrigger: {
      trigger: heroRef.value,
      start: "top top",
      end: "bottom top",
      scrub: 1,
    },
  });

  gsap.to(logoRef.value, {
    y: -40,
    scale: 1.05,
    ease: "none",
    scrollTrigger: {
      trigger: heroRef.value,
      start: "top top",
      end: "bottom top",
      scrub: 1,
    },
  });

  gsap.to(astronautRef.value, {
    y: -120,
    ease: "none",
    scrollTrigger: {
      trigger: heroRef.value,
      start: "top top",
      end: "bottom top",
      scrub: 1,
    },
  });

  gsap.to(heroRef.value, {
    scale: 0.85,
    autoAlpha: 0,
    ease: "none",
    scrollTrigger: {
      trigger: heroRef.value,
      start: "60% top",
      end: "bottom top",
      scrub: 1,
    },
  });
}

function playReveal() {
  if (revealPlayed) return;
  revealPlayed = true;

  const tl = gsap.timeline({ delay: 0.1 });

  tl.fromTo(
    logoRef.value,
    { autoAlpha: 0, filter: "blur(30px)", scale: 0.6 },
    {
      autoAlpha: 1,
      filter: "blur(0px)",
      scale: 1,
      duration: 2,
      ease: "expo.out",
    }
  );

  tl.fromTo(
    astronautRef.value,
    { autoAlpha: 0, y: 100, rotate: -8, scale: 0.9 },
    {
      autoAlpha: 1,
      y: 0,
      rotate: 0,
      scale: 1,
      duration: 2.2,
      ease: "expo.out",
    },
    "-=1.4"
  );

  tl.fromTo(
    scrollIndicatorRef.value,
    { autoAlpha: 0, y: 20 },
    { autoAlpha: 1, y: 0, duration: 1, ease: "power3.out" },
    "-=0.8"
  );

  tl.to(
    ".hero__scroll-line",
    {
      scaleY: 1,
      duration: 1.2,
      ease: "power2.inOut",
      repeat: -1,
      yoyo: true,
    },
    "-=0.5"
  );

  setupScrollAnimations();
}

onMounted(() => {
  ctx = gsap.context(() => {
    gsap.set([logoRef.value, astronautRef.value, scrollIndicatorRef.value], {
      autoAlpha: 0,
    });

    if (preloaderState.lensComplete) {
      playReveal();
    }
  }, heroRef.value);
});

watch(
  () => preloaderState.lensComplete,
  (done) => {
    if (done && !revealPlayed) {
      playReveal();
    }
  }
);

onUnmounted(() => {
  if (ctx) ctx.revert();
});
</script>

<style scoped>
.hero {
  position: relative;
  height: 100vh;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  transform-style: preserve-3d;
  perspective: 1200px;
}

.hero__bg {
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse at 50% 30%, #0f0f2e 0%, #050510 70%);
  z-index: 0;
}

.hero__bg::after {
  content: "";
  position: absolute;
  inset: 0;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  background-repeat: repeat;
  background-size: 256px 256px;
  opacity: 0.04;
}

.hero__nebula {
  position: absolute;
  inset: -20%;
  background: radial-gradient(
      ellipse at 30% 40%,
      rgba(167, 139, 250, 0.08) 0%,
      transparent 50%
    ),
    radial-gradient(
      ellipse at 70% 60%,
      rgba(96, 165, 250, 0.06) 0%,
      transparent 50%
    ),
    radial-gradient(
      ellipse at 50% 20%,
      rgba(251, 191, 36, 0.03) 0%,
      transparent 40%
    );
  z-index: 1;
  animation: nebulaDrift 20s ease-in-out infinite alternate;
  will-change: transform;
}

@keyframes nebulaDrift {
  0% {
    transform: translate(0, 0) scale(1);
  }
  100% {
    transform: translate(30px, -20px) scale(1.05);
  }
}

.hero__content {
  position: relative;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 1200px;
  padding: 0;
  height: 70vh;
}

.hero__logo {
  position: absolute;
  width: clamp(400px, 85vw, 1200px);
  filter: brightness(1.5) contrast(1.1);
  visibility: hidden;
  z-index: 1;
  will-change: transform;
}

.hero__astronaut {
  position: relative;
  width: clamp(400px, 60vw, 950px);
  filter: drop-shadow(0 0 60px rgba(167, 139, 250, 0.15))
    drop-shadow(0 0 120px rgba(96, 165, 250, 0.08));
  visibility: hidden;
  animation: float 6s ease-in-out infinite;
  z-index: 2;
  will-change: transform;
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-15px);
  }
}

.hero__scroll-indicator {
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  visibility: hidden;
}

.hero__scroll-line {
  width: 1px;
  height: 40px;
  background: linear-gradient(to bottom, transparent, var(--color-accent));
  transform-origin: top;
  transform: scaleY(0);
}

.hero__transition-out {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 200px;
  background: linear-gradient(to bottom, transparent, var(--color-bg-deep));
  z-index: 6;
  pointer-events: none;
}

@media (max-width: 768px) {
  .hero__content {
    padding: 0 1.5rem;
  }
}
</style>
